import { FC, useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Center, Text } from "@/components/ui";
import { useLocalization, useThemeColors } from "@/hooks";

export interface LineChartPoint {
  x: number;
  y: number;
}

export interface LineSeries {
  color?: string;
  key: string;
  label: string;
  points: LineChartPoint[];
}

export interface LineChartReferencePoint {
  color?: string;
  label?: string;
  seriesKey?: string;
  x: number;
  y: number;
}

/** Vertical reference line at a given x value, spanning the full
    chart height. Stronger emphasis than `LineChartReferencePoint`
    (which is a dot at one (x, y)) — use this when the x value is
    the headline metric and needs to read at a glance. */
export interface LineChartReferenceLine {
  color?: string;
  label?: string;
  x: number;
}

interface LineChartProps {
  formatX?: (value: number) => string;
  formatY?: (value: number) => string;
  height?: number;
  /** Minimum chart width in px. The chart fills the available width but
      never shrinks below this — it scrolls horizontally instead, so the
      x-axis stays readable on narrow screens. */
  minWidth?: number;
  referenceLines?: LineChartReferenceLine[];
  referencePoints?: LineChartReferencePoint[];
  series: LineSeries[];
  xDomain?: [number, number];
  xLabel?: string;
  yDomain?: [number, number];
  yLabel?: string;
}

const TICK_COLOR = "hsl(220,10%,46%)";
const GRID_STROKE = "hsl(220,14%,93%)";

interface MergedRow {
  x: number;
  [seriesKey: string]: number;
}

function LineTooltip({
  active,
  formatX,
  formatY,
  payload,
  seriesByKey,
  xLabel,
}: {
  active?: boolean;
  formatX: (v: number) => string;
  formatY: (v: number) => string;
  payload?: { color?: string; dataKey: string; value: number }[];
  seriesByKey: Record<string, LineSeries>;
  xLabel?: string;
}) {
  if (!active || !payload?.length) return null;
  const x = (payload[0] as unknown as { payload: { x: number } }).payload.x;
  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        fontFamily: "inherit",
        minWidth: 160,
        padding: "10px 12px",
      }}
    >
      <p
        style={{
          color: "hsl(222,20%,16%)",
          fontSize: 12,
          fontWeight: 600,
          margin: 0,
        }}
      >
        {xLabel ? `${xLabel}: ` : ""}
        {formatX(x)}
      </p>
      <div style={{ marginTop: 6 }}>
        {payload.map((p) => {
          const series = seriesByKey[p.dataKey];
          if (!series) return null;
          return (
            <div
              key={p.dataKey}
              style={{
                alignItems: "center",
                display: "flex",
                fontSize: 12,
                gap: 6,
                marginTop: 2,
              }}
            >
              <span
                style={{
                  background: p.color ?? series.color ?? "hsl(222,20%,16%)",
                  borderRadius: 6,
                  display: "inline-block",
                  height: 8,
                  width: 8,
                }}
              />
              <span style={{ color: "hsl(220,10%,40%)", flex: 1 }}>
                {series.label}
              </span>
              <span style={{ color: "hsl(222,20%,16%)", fontWeight: 600 }}>
                {formatY(p.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const DEFAULT_PALETTE = [
  "hsl(217, 91%, 60%)",
  "hsl(160, 84%, 39%)",
  "hsl(38, 92%, 50%)",
  "hsl(0, 84%, 60%)",
  "hsl(280, 70%, 55%)",
  "hsl(190, 80%, 45%)",
];

const LineChart: FC<LineChartProps> = ({
  formatX = (v) => String(v),
  formatY = (v) => String(v),
  height = 380,
  minWidth = 520,
  referenceLines = [],
  referencePoints = [],
  series,
  xDomain,
  xLabel,
  yDomain,
  yLabel,
}) => {
  const { i18n } = useLocalization();
  const themeColors = useThemeColors();

  const { data, seriesByKey, seriesWithColor } = useMemo(() => {
    const xs = new Set<number>();
    series.forEach((s) => s.points.forEach((p) => xs.add(p.x)));
    const sortedX = Array.from(xs).sort((a, b) => a - b);

    const rows: MergedRow[] = sortedX.map((x) => {
      const row: MergedRow = { x };
      series.forEach((s) => {
        const point = s.points.find((p) => p.x === x);
        if (point) row[s.key] = point.y;
      });
      return row;
    });

    const colored = series.map((s, i) => ({
      ...s,
      color: s.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length],
    }));

    const byKey: Record<string, LineSeries> = {};
    colored.forEach((s) => {
      byKey[s.key] = s;
    });

    return { data: rows, seriesByKey: byKey, seriesWithColor: colored };
  }, [series]);

  if (data.length === 0) {
    return (
      <Center className="w-full" style={{ height }}>
        <Text className="text-typography-500">
          {i18n.t("dashboardNavigator.noData")}
        </Text>
      </Center>
    );
  }

  /* Plain `<div>`s for the scroll wrapper (not RN `<View>`/`<Box>`): RN Web
     doesn't expose `overflowX` and only applies the shorthand `overflow`,
     which would clip vertical tooltips. `minWidth: 0` on the outer wrapper
     lets it shrink below the inner content so the card keeps its flex share
     and the overflow scrolls horizontally instead of stretching the layout. */
  return (
    <div style={{ minWidth: 0, overflowX: "auto", width: "100%" }}>
      <div style={{ height, minWidth, width: "100%" }}>
        {/* Explicit pixel height on the ResponsiveContainer (instead of
            `height="100%"`) avoids the "width(-1) and height(-1)"
            warning Recharts fires when the parent hasn't been measured
            yet on the very first paint — the chart sits inside a React
            Native `<View>` whose dimensions only resolve after layout. */}
        <ResponsiveContainer height={height} minWidth={0} width="100%">
          <RechartsLineChart
            data={data}
            margin={{ bottom: xLabel ? 28 : 12, left: 4, right: 24, top: 16 }}
          >
            <CartesianGrid
              stroke={GRID_STROKE}
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              axisLine={false}
              dataKey="x"
              label={
                xLabel
                  ? {
                      fill: TICK_COLOR,
                      fontSize: 11,
                      offset: 0,
                      position: "insideBottom",
                      value: xLabel,
                    }
                  : undefined
              }
              tick={{ fill: TICK_COLOR, fontSize: 11 }}
              tickFormatter={formatX}
              tickLine={false}
              type="number"
            />
            <YAxis
              axisLine={false}
              domain={yDomain ?? ["auto", "auto"]}
              label={
                yLabel
                  ? {
                      angle: -90,
                      fill: TICK_COLOR,
                      fontSize: 11,
                      position: "insideLeft",
                      value: yLabel,
                    }
                  : undefined
              }
              tick={{ fill: TICK_COLOR, fontSize: 11 }}
              tickFormatter={formatY}
              tickLine={false}
            />
            <Tooltip
              content={
                <LineTooltip
                  formatX={formatX}
                  formatY={formatY}
                  seriesByKey={seriesByKey}
                  xLabel={xLabel}
                />
              }
              cursor={{ stroke: GRID_STROKE, strokeWidth: 1 }}
            />
            <Legend
              iconSize={10}
              iconType="circle"
              itemSorter={null}
              wrapperStyle={{
                color: "hsl(220,10%,40%)",
                fontSize: 12,
                paddingTop: 8,
              }}
            />
            {seriesWithColor.map((s) => (
              <Line
                activeDot={{ fill: s.color, r: 5, strokeWidth: 0 }}
                animationDuration={600}
                dataKey={s.key}
                dot={false}
                isAnimationActive={true}
                key={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                type="monotone"
              />
            ))}
            {referenceLines.map((rl, i) => {
              const color = rl.color ?? themeColors.primary;
              return (
                <ReferenceLine
                  key={`refline-${rl.label ?? "line"}-${i}`}
                  label={
                    rl.label
                      ? {
                          fill: color,
                          fontSize: 11,
                          fontWeight: 700,
                          position: "insideTopRight",
                          value: rl.label,
                        }
                      : undefined
                  }
                  stroke={color}
                  strokeDasharray="6 4"
                  strokeWidth={2}
                  x={rl.x}
                />
              );
            })}
            {referencePoints.map((rp, i) => (
              <ReferenceDot
                fill={rp.color ?? themeColors.primary}
                key={`${rp.label ?? "dot"}-${i}`}
                label={
                  rp.label
                    ? {
                        fill: "hsl(222,20%,16%)",
                        fontSize: 11,
                        fontWeight: 600,
                        offset: 10,
                        position: "top",
                        value: rp.label,
                      }
                    : undefined
                }
                r={5}
                stroke="white"
                strokeWidth={2}
                x={rp.x}
                y={rp.y}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LineChart;
